import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { TenantDivisionListItemSchema } from '@/entities/tenants-divisions/api/generated/model';
import { getAvailableYears } from '@/entities/tenants-divisions/lib/divisions-by-year';
import { TenantUserRoleSchema } from '@/entities/tenantusers/api/generated/model';
import { getCurrentFiscalYear } from '@/shared/lib/fiscal-year';
import { Button, Form, Grid, InputField, SelectField } from '@/shared/ui';
import { FormErrorSummary } from '@/shared/ui/form/form-error-summary/FormErrorSummary';
import { SideMenuFormFooter } from '@/widgets/right-side-menu/ui/SideMenuFormFooter';
import { DivisionsByYearField } from './DivisionsByYearField';
import { type UserCreateFormValues, userCreateSchema } from './schema';
import { useUserCreateMutation } from './useUserCreateMutation';

type SelectOption = { label: string; value: string };

type Props = {
  onClose: () => void;
  divisionItems: TenantDivisionListItemSchema[];
  roleOptions: SelectOption[];
};

/**
 * 新規ユーザー作成時の初期年度を決定する
 *
 * - 当年度に組織データがあれば当年度
 * - なければ利用可能な年度のうち最新（=末尾、昇順ソート済み）
 * - 何も無ければ当年度を文字列で返す（API 呼び出し時に弾かれる想定）
 */
const resolveInitialYear = (availableYears: string[]): string => {
  const currentFiscalYear = String(getCurrentFiscalYear());
  if (availableYears.includes(currentFiscalYear)) return currentFiscalYear;
  return availableYears.at(-1) ?? currentFiscalYear;
};

export const UserCreateForm = ({ onClose, divisionItems, roleOptions }: Props) => {
  const { t } = useTranslation();

  // React Compiler が自動メモ化するため useMemo は使用しない
  const availableYears = getAvailableYears(divisionItems);
  const initialYear = resolveInitialYear(availableYears);

  const form = useForm<UserCreateFormValues>({
    resolver: zodResolver(userCreateSchema),
    defaultValues: {
      email: '',
      name: '',
      divisions: [{ year: initialYear, division_id: '' }],
      role: TenantUserRoleSchema.editor,
      password: '',
    },
  });

  const createMutation = useUserCreateMutation({ onSuccess: onClose });
  const isPending = createMutation.isPending;

  const handleSubmit = (values: UserCreateFormValues) => {
    createMutation.mutate({
      data: {
        email: values.email,
        name: values.name,
        division_ids: values.divisions.map((d) => d.division_id),
        role: values.role,
        password: values.password,
      },
    });
  };

  const fieldLabels = {
    email: t('user-management.field.email'),
    name: t('user-management.field.name'),
    divisions: t('user-management.field.division'),
    role: t('user-management.field.role'),
    password: t('user-management.field.password'),
  };

  return (
    <Form.Provider {...form}>
      <Form
        leavePageAlertCondition={{
          dirtyFields: form.formState.dirtyFields,
          isSubmitting: form.formState.isSubmitting,
          isSubmitSuccessful: form.formState.isSubmitSuccessful,
        }}
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex min-h-0 flex-1 flex-col"
      >
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          <FormErrorSummary
            errors={form.formState.errors}
            isSubmitted={form.formState.isSubmitted}
            submitCount={form.formState.submitCount}
            fieldLabels={fieldLabels}
          />
          <Grid
            columns={1}
            items={[
              [
                {
                  key: 'email',
                  field: (
                    <InputField
                      control={form.control}
                      name="email"
                      label={t('user-management.field.email')}
                      type="email"
                      required
                      autoComplete={false}
                    />
                  ),
                },
                {
                  key: 'name',
                  field: (
                    <InputField
                      control={form.control}
                      name="name"
                      label={t('user-management.field.name')}
                      required
                      autoComplete={false}
                    />
                  ),
                },
                {
                  key: 'divisions',
                  field: <DivisionsByYearField divisionItems={divisionItems} />,
                },
                {
                  key: 'role',
                  field: (
                    <SelectField
                      control={form.control}
                      name="role"
                      label={t('user-management.field.role')}
                      options={roleOptions}
                      required
                    />
                  ),
                },
                {
                  key: 'password',
                  field: (
                    <InputField
                      control={form.control}
                      name="password"
                      type="password"
                      label={t('user-management.field.password')}
                      required
                      autoComplete="new-password"
                      supportText={t('user-management.users.password-rule')}
                      showSupportText
                    />
                  ),
                },
              ],
            ]}
          />
        </div>
        <SideMenuFormFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
            {t('common.actions.cancel')}
          </Button>
          <Button type="submit" disabled={isPending}>
            {t('common.actions.save')}
          </Button>
        </SideMenuFormFooter>
      </Form>
    </Form.Provider>
  );
};
